import React from 'react';
import { Clock, Edit3, Trash2, Calendar, User } from 'lucide-react';
import { motion } from 'motion/react';
import { format } from 'date-fns';

export default function BlogCard({
  post,
  isAdmin,
  onEdit,
  onDelete,
  onClick,
}) {
  const getTagStyle = (tag) => {
    const t = tag.toLowerCase();
    if (t.includes('healing') || t.includes('soil')) {
      return 'bg-brand-green-light/10 text-brand-green-dark border-brand-green-light/25';
    }
    if (t.includes('process') || t.includes('method')) {
      return 'bg-brand-gold/10 text-brand-gold-dark border-brand-gold/25';
    }
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const id = post._id;
  const dateStr = post.createdAt ? format(new Date(post.createdAt), 'MMM d, yyyy') : '';

  return (
    <motion.div
      layout
      whileHover={{ y: -6, boxShadow: '0 12px 30px -10px rgba(19, 43, 33, 0.08)' }}
      id={`blog-card-${id}`}
      className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-[0_4px_20px_-4px_rgba(0,0,0,0.03)] hover:border-brand-gold/30 transition-all duration-300 flex flex-col h-full"
    >
      {post.coverImage && (
        <div 
          onClick={() => onClick(post)}
          className="relative h-52 w-full overflow-hidden cursor-pointer group"
        >
          <img
            src={post.coverImage}
            alt={post.title}
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          
          {isAdmin && post.status === 'draft' && (
            <span className="absolute top-4 left-4 bg-brand-gold text-brand-cream-light font-sans text-xs font-semibold tracking-wider uppercase py-1 px-2.5 rounded-md shadow-sm border border-brand-gold-dark/20">
              Draft
            </span>
          )}
        </div>
      )}

      <div className="p-6 md:p-7 flex flex-col flex-grow relative">
        {isAdmin && (
          <div className="absolute top-5 right-5 flex items-center gap-2.5 z-10 bg-white/90 backdrop-blur-sm p-1.5 rounded-lg border border-gray-100/50 shadow-sm">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(post);
              }}
              title="Edit Post"
              className="p-1.5 text-gray-400 hover:text-brand-green-medium hover:bg-brand-cream rounded transition-colors cursor-pointer"
            >
              <Edit3 className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(id);
              }}
              title="Delete Post"
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}

        <div 
          onClick={() => onClick(post)}
          className="cursor-pointer flex-grow flex flex-col gap-3"
        >
          <div className="flex items-center gap-3.5 text-xs text-gray-400 font-medium tracking-wide">
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5 text-brand-gold/70" />
              {dateStr}
            </span>
            {post.readTime && (
              <>
                <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-brand-gold/70" />
                  {post.readTime}
                </span>
              </>
            )}
          </div>

          <h3 className="font-serif text-xl md:text-2xl text-brand-green-dark leading-snug group-hover:text-brand-gold font-medium line-clamp-2 pr-16">
            {post.title}
          </h3>

          <p className="text-gray-500 text-sm leading-relaxed line-clamp-3">
            {post.subtitle || post.excerpt}
          </p>
        </div>

        <div className="mt-6 pt-5 border-t border-gray-50/80 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-brand-cream flex items-center justify-center border border-brand-gold/20">
              <User className="w-3.5 h-3.5 text-brand-gold" />
            </div>
            <span className="text-xs font-medium text-brand-green-medium">
              Amelia Downie
            </span>
          </div>

          <div className="flex flex-wrap gap-1.5 max-w-[60%] justify-end">
            {(post.tags || []).slice(0, 2).map((tag) => (
              <span
                key={tag}
                className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${getTagStyle(tag)}`}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
